import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles as SparkIcon, Volume2, VolumeX } from "lucide-react";

const STORAGE_KEY = "miara_portal_entered_v1";

const PortalIntro = ({ onEnter }) => {
  const [open, setOpen] = useState(false);
  const [entering, setEntering] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  // Show only once per browser
  useEffect(() => {
    const already = sessionStorage.getItem(STORAGE_KEY);
    if (!already) {
      setOpen(true);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Force play once the video mounts (some browsers refuse autoplay attribute alone)
  useEffect(() => {
    if (open && videoRef.current) {
      const v = videoRef.current;
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // ignore — fallback poster will show
        });
      }
    }
  }, [open]);

  const enter = () => {
    setEntering(true);
    window.dispatchEvent(new CustomEvent("miara:enter"));
    setTimeout(() => {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(false);
      document.body.style.overflow = "";
      onEnter && onEnter();
    }, 1400);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="portal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[100] bg-black overflow-hidden"
          data-testid="portal-intro"
        >
          {/* Video background (with poster fallback if codec unsupported) */}
          <motion.video
            ref={videoRef}
            src="/intro/portal.mp4"
            poster="/intro/portal_poster.jpg"
            autoPlay
            muted
            playsInline
            loop
            preload="auto"
            initial={{ scale: 1.05 }}
            animate={entering ? { scale: 1.6 } : { scale: 1.05 }}
            transition={{ duration: entering ? 1.4 : 8, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Soft vignette so text is readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/85" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.55)_85%)]" />

          {/* Floating sparkle ornaments */}
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 30 }).map((_, i) => (
              <span
                key={i}
                className="sparkle"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: `${2 + Math.random() * 4}px`,
                  height: `${2 + Math.random() * 4}px`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                }}
              />
            ))}
          </div>

          {/* Mute toggle */}
          <button
            onClick={toggleMute}
            data-testid="portal-mute-toggle"
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md border border-amber-400/30 text-amber-200 flex items-center justify-center hover:border-amber-300/70 transition"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          {/* Center content */}
          <AnimatePresence>
            {!entering && (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 1, delay: 0.6 }}
                className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
              >
                <SparkIcon className="text-amber-300 mb-5 glow-pulse" size={34} />
                <span className="font-accent text-amber-300 text-xl md:text-2xl tracking-wide">
                  An undiscovered land awaits
                </span>
                <h1
                  className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mt-4 hero-text-shadow leading-[1.05] max-w-4xl"
                  data-testid="portal-title"
                >
                  Welcome to{" "}
                  <span className="italic text-gradient-gold">Van Grotto</span>
                </h1>
                <p className="mt-6 text-emerald-50/85 max-w-xl text-base md:text-lg hero-text-shadow">
                  Step between the two ancient willows and slip into the world
                  of The Carmel Prince of Miara Mountain.
                </p>

                <motion.button
                  onClick={enter}
                  data-testid="portal-enter"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  className="mt-10 btn-gold !text-base !py-3.5 !px-8 glow-pulse"
                >
                  <SparkIcon size={18} /> Enter the Grotto
                </motion.button>

                <button
                  onClick={enter}
                  data-testid="portal-skip"
                  className="mt-4 text-xs uppercase tracking-[0.3em] text-emerald-50/55 hover:text-amber-300 transition"
                >
                  skip
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Entering "warp" white flash */}
          <AnimatePresence>
            {entering && (
              <motion.div
                key="flash"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4 }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at center, rgba(255,232,154,0.85) 0%, rgba(255,215,0,0.5) 30%, transparent 75%)",
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PortalIntro;
